import { newAmount } from '@orus.eu/amount'
import { nbsp } from '@orus.eu/char'
import {
  AmountDimension,
  BooleanDimension,
  CalendarDateDimension,
  ExclusionDimension,
  FinancialRateDimension,
  IntegerDimension,
  mrpwSelectedDimension,
  StringsWithDataEnumDimension,
  TextDimension,
} from '@orus.eu/dimensions'
import { mrpwProtectionEntries } from '@orus.eu/pricing'

import { MrpwWakamActivityDimension } from './mrpw-activity-vocabulary.ts'
import { MrpwAddressValidationResultDimension } from './mrpw-address-validation-result-dimension.ts'
import {
  MrpwDerogationAssetsValueDimension,
  MrpwDerogationRangeAssetsValueDimension,
} from './mrpw-derogation-assets-value-dimension.ts'
import { MrpwQuoteDimension, MrpwQuoteV3Dimension } from './mrpw-quote-dimension.ts'
import { MrpwWakamAllowedCityCodesDimension } from './mrpw-wakam-allowed-city-codes-dimension.ts'

export const mrpwActivityAddressAllowedWakamCitiesDimension = new MrpwWakamAllowedCityCodesDimension({
  name: 'mrpwActivityAddressAllowedWakamCities',
  displayKeys: { name: 'dimension_name_mrpw_activity_address_allowed_wakam_cities' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwActivityAddressValidationResultDimension = new MrpwAddressValidationResultDimension({
  name: 'mrpwActivityAddressValidationResult',
  displayKeys: { name: 'dimension_name_mrpw_activity_address_validation_result' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwWakamSecondaryActivityDimension = new MrpwWakamActivityDimension({
  name: 'mrpwWakamSecondaryActivity',
  displayKeys: { name: 'dimension_name_mrpw_wakam_secondary_activity' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwWakamTertiaryActivityDimension = new MrpwWakamActivityDimension({
  name: 'mrpwWakamTertiaryActivity',
  displayKeys: { name: 'dimension_name_mrpw_wakam_tertiary_activity' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwHeadCountDimension = new IntegerDimension({
  name: 'mrpwHeadCount',
  displayKeys: {
    name: 'dimension_name_mrpw_head_count',
    hint: 'dimension_hint_mrpw_head_count',
    placeholder: 'dimension_placeholder_mrpw_head_count',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwAssetsClaimsThreeYearsDimension = new StringsWithDataEnumDimension({
  name: 'mrpwAssetsClaimsThreeYears',
  displayKeys: {
    name: 'dimension_name_mrpw_assets_claims_three_years',
    hint: 'dimension_hint_mrpw_assets_claims_three_years',
  },
  entries: [
    ['0', { label: '0' }],
    ['1', { label: '1' }],
    ['2', { label: '2' }],
    ['3+', { label: '3+' }],
  ],
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwOccupationStatus = 'Locataire' | 'Propriétaire Exploitant' | 'Colocataire' | 'Copropriétaire'

export const mrpwOccupationStatusEntries: [MrpwOccupationStatus, { label: string }][] = [
  ['Locataire', { label: 'Locataire' }],
  ['Propriétaire Exploitant', { label: 'Propriétaire' }],
  ['Colocataire', { label: 'Colocataire' }],
  ['Copropriétaire', { label: 'Copropriétaire' }],
]

export const mrpwOccupationStatusDimension = new StringsWithDataEnumDimension({
  name: 'mrpwOccupationStatus',
  displayKeys: {
    name: 'dimension_name_mrpw_occupation_status',
    placeholder: 'dimension_placeholder_mrpw_occupation_status',
  },
  entries: mrpwOccupationStatusEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwMonumentDimension = new BooleanDimension({
  name: 'mrpwMonument',
  displayKeys: { name: 'dimension_name_mrpw_monument' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwHardBuildingDimension = new BooleanDimension({
  name: 'mrpwHardBuilding',
  displayKeys: { name: 'dimension_name_mrpw_hard_building' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwDerogationRcDimension = new FinancialRateDimension({
  name: 'mrpwDerogationRc',
  displayKeys: { name: 'dimension_name_mrpw_derogation_rc' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwDerogationDmgDimension = new FinancialRateDimension({
  name: 'mrpwDerogationDmg',
  displayKeys: { name: 'dimension_name_mrpw_derogation_dmg' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwDerogationIncPePvvDimension = new FinancialRateDimension({
  name: 'mrpwDerogationIncPePvv',
  displayKeys: { name: 'dimension_name_mrpw_derogation_inc_pe_pvv' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwMultisiteDimension = new BooleanDimension({
  name: 'mrpwMultisite',
  displayKeys: { name: 'dimension_name_mrpw_multisite', hint: 'dimension_hint_mrpw_multisite' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwOptionPjDimension = new BooleanDimension({
  name: 'mrpwOptionPj',
  displayKeys: { name: 'dimension_name_mrpw_option_pj' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwOptionPeDimension = new BooleanDimension({
  name: 'mrpwOptionPe',
  displayKeys: { name: 'dimension_name_mrpw_option_pe' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwOptionPvvDimension = new BooleanDimension({
  name: 'mrpwOptionPvv',
  displayKeys: { name: 'dimension_name_mrpw_option_pvv' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwOptionBdgDimension = new BooleanDimension({
  name: 'mrpwOptionBdg',
  displayKeys: { name: 'dimension_name_mrpw_option_bdg' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwOptionRcTravauxDimension = new BooleanDimension({
  name: 'mrpwOptionRcTravaux',
  displayKeys: { name: 'dimension_name_mrpw_option_rc_travaux' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwRcTravauxStartDimension = new CalendarDateDimension({
  name: 'mrpwRcTravauxStart',
  displayKeys: { name: 'dimension_name_mrpw_rc_travaux_start' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwRcTravauxEndDimension = new CalendarDateDimension({
  name: 'mrpwRcTravauxEnd',
  displayKeys: { name: 'dimension_name_mrpw_rc_travaux_end' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwOptionBoostDimension = new BooleanDimension({
  name: 'mrpwOptionBoost',
  displayKeys: { name: 'dimension_name_mrpw_option_boost' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwPremiumOverchargesRcDimension = new FinancialRateDimension({
  name: 'mrpwPremiumOverchargesRC',
  displayKeys: { name: 'dimension_name_mrpw_premium_overcharges_r_c' },
  configuration: {
    min: -0.1,
    max: 5,
    step: 0.01,
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwPremiumOverchargesDmgDimension = new FinancialRateDimension({
  name: 'mrpwPremiumOverchargesDMG',
  displayKeys: { name: 'dimension_name_mrpw_premium_overcharges_d_m_g' },
  configuration: {
    min: -0.1,
    max: 5,
    step: 0.01,
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwPremiumOverchargesIncPePvvDimension = new FinancialRateDimension({
  name: 'mrpwPremiumOverchargesIncPePvv',
  displayKeys: { name: 'dimension_name_mrpw_premium_overcharges_inc_pe_pvv' },
  configuration: {
    min: -0.1,
    max: 5,
    step: 0.01,
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwQuoteDimension = new MrpwQuoteDimension({
  name: 'mrpwQuote',
  displayKeys: { name: 'dimension_name_mrpw_quote' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwQuoteV3Dimension = new MrpwQuoteV3Dimension({
  name: 'mrpwQuoteV3',
  displayKeys: { name: 'dimension_name_mrpw_quote_v3' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
  computedAfterTimestamp: 1751891253000, // Monday, July 7, 2025 2:27:33 PM GMT+02:00 DST
} as const)

// returned by the wakam API
export const mrpwRequiredProtectionLevelDimension = new StringsWithDataEnumDimension({
  name: 'mrpwRequiredProtectionLevel',
  displayKeys: {
    name: 'dimension_name_mrpw_required_protection_level',
    hint: 'dimension_hint_mrpw_required_protection_level',
  },
  entries: mrpwProtectionEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwNewlyCreatedCompanyDimension = new BooleanDimension({
  name: 'mrpwNewlyCreatedCompany',
  displayKeys: { name: 'dimension_name_mrpw_newly_created_company' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwActivityCoffeeShopDimension = new BooleanDimension({
  name: 'mrpwActivityCoffeeShop',
  displayKeys: { name: 'dimension_name_mrpw_activity_coffee_shop' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwActivityPharmacieDimension = new BooleanDimension({
  name: 'mrpwActivityPharmacie',
  displayKeys: { name: 'dimension_name_mrpw_activity_pharmacie' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwMarchandiseConserveeDimension = new BooleanDimension({
  name: 'mrpwMarchandiseConservee',
  displayKeys: { name: 'dimension_name_mrpw_marchandise_conservee' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwRcpDimension = new BooleanDimension({
  name: 'mrpwRcp',
  displayKeys: { name: 'dimension_name_mrpw_rcp' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwRevenueOneHundredAndFiftyPctDimension = new AmountDimension({
  name: 'mrpwRevenueOneHundredAndFiftyPct',
  displayKeys: {
    name: 'dimension_name_mrpw_revenue_one_hundred_and_fifty_pct',
    hint: 'dimension_hint_mrpw_revenue_one_hundred_and_fifty_pct',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwRevenueFifteenPctDimension = new AmountDimension({
  name: 'mrpwRevenueFifteenPct',
  displayKeys: { name: 'dimension_name_mrpw_revenue_fifteen_pct', hint: 'dimension_hint_mrpw_revenue_fifteen_pct' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwAssetsValueTwentyPctDimension = new AmountDimension({
  name: 'mrpwAssetsValueTwentyPct',
  displayKeys: { name: 'dimension_name_mrpw_assets_value_twenty_pct' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwAssetsValueFiftyPctDimension = new AmountDimension({
  name: 'mrpwAssetsValueFiftyPct',
  displayKeys: { name: 'dimension_name_mrpw_assets_value_fifty_pct' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwYearlyBasePremiumDimension = new AmountDimension({
  name: 'mrpwYearlyBasePremium',
  displayKeys: { name: 'dimension_name_mrpw_yearly_base_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwYearlyTaxesDimension = new AmountDimension({
  name: 'mrpwYearlyTaxes',
  displayKeys: { name: 'dimension_name_mrpw_yearly_taxes' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwYearlyTotalPremiumDimension = new AmountDimension({
  name: 'mrpwYearlyTotalPremium',
  displayKeys: { name: 'dimension_name_mrpw_yearly_total_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: true,
  inputLocking: undefined,
} as const)
export const mrpwTerrorismTaxDimension = new AmountDimension({
  name: 'mrpwTerrorismTax',
  displayKeys: { name: 'dimension_name_mrpw_terrorism_tax' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwQuoteInputExclusionsDimension = new ExclusionDimension({
  name: 'mrpwQuoteInputExclusions',
  displayKeys: { name: 'dimension_name_mrpw_quote_input_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwExclusionsDimension = new ExclusionDimension({
  name: 'mrpwExclusions',
  displayKeys: { name: 'dimension_name_mrpw_exclusions' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: false },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwPizzeriaStatusDimension = new BooleanDimension({
  name: 'mrpwPizzeriaStatus',
  displayKeys: { name: 'dimension_name_mrpw_pizzeria_status' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwManagementExperienceInPizzeriaRequiredDimension = new BooleanDimension({
  name: 'mrpwManagementExperienceInPizzeriaRequired',
  displayKeys: { name: 'dimension_name_mrpw_management_experience_in_pizzeria_required' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwHasManagementExperienceInPizzeriaDimension = new BooleanDimension({
  name: 'mrpwHasManagementExperienceInPizzeria',
  displayKeys: { name: 'dimension_name_mrpw_has_management_experience_in_pizzeria' },
  considerForDifferenceComputationInUi: { client: true, partner: true, platform: true },
  includeInTrackingMessages: false,
  inputLocking: {
    lockedInQuoteFor: {
      client: mrpwSelectedDimension,
    },
  },
} as const)

export const mrpwNaturalDisasterPremiumDimension = new AmountDimension({
  name: 'mrpwNaturalDisasterPremium',
  displayKeys: { name: 'dimension_name_mrpw_natural_disaster_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwAssistancePremiumDimension = new AmountDimension({
  name: 'mrpwAssistancePremium',
  displayKeys: { name: 'dimension_name_mrpw_assistance_premium' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwHighRevenueDimension = new BooleanDimension({
  name: 'mrpwHighRevenue',
  displayKeys: { name: 'dimension_name_mrpw_high_revenue', hint: 'dimension_hint_mrpw_high_revenue' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
export const mrpwHighRevenueThreshold = newAmount(1_500_000)

export const mrpwDerogationRangeAssetsValueDimension = new MrpwDerogationRangeAssetsValueDimension({
  name: 'mrpwDerogationRangeAssetsValue',
  displayKeys: { name: 'dimension_name_mrpw_derogation_range_assets_value' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwDerogationAssetsValueDimension = new MrpwDerogationAssetsValueDimension({
  name: 'mrpwDerogationAssetsValue',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_assets_value',
    placeholder: 'dimension_placeholder_mrpw_derogation_assets_value',
  },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationGeneralDeductible = 'DEFAULT' | '1 000' | '1 500'

export const MrpwDerogationGeneralDeductibleEntries: [MrpwDerogationGeneralDeductible, { label: string }][] = [
  ['DEFAULT', { label: `450${nbsp}€ (par défaut)` }],
  ['1 000', { label: `1 000${nbsp}€` }],
  ['1 500', { label: `1 500${nbsp}€` }],
]

export const mrpwDerogationGeneralDeductibleDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationGeneralDeductible',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_general_deductible',
    placeholder: 'dimension_placeholder_mrpw_derogation_general_deductible',
  },
  entries: MrpwDerogationGeneralDeductibleEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationStolenAssetsValue = 'DEFAULT' | '10%' | '20%' | '75%' | '100%'

export const MrpwDerogationStolenAssetsValueEntries: [MrpwDerogationStolenAssetsValue, { label: string }][] = [
  ['10%', { label: `10${nbsp}%` }],
  ['20%', { label: `20${nbsp}%` }],
  ['DEFAULT', { label: `50${nbsp}% (par défaut)` }],
  ['75%', { label: `75${nbsp}%` }],
  ['100%', { label: `100${nbsp}%` }],
]

export const mrpwDerogationStolenAssetsValueDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationStolenAssetsValue',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_stolen_assets_value',
    placeholder: 'dimension_placeholder_mrpw_derogation_stolen_assets_value',
  },
  entries: MrpwDerogationStolenAssetsValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationCashAndValues = 'DEFAULT' | '10 000' | '15 000' | '20 000'

export const MrpwDerogationCashAndValuesEntries: [MrpwDerogationCashAndValues, { label: string }][] = [
  ['DEFAULT', { label: `3 000${nbsp}€ (par défaut)` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['15 000', { label: `15 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
]

export const mrpwDerogationCashAndValuesDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationCashAndValues',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_cash_and_values',
    placeholder: 'dimension_placeholder_mrpw_derogation_cash_and_values',
  },
  entries: MrpwDerogationCashAndValuesEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationBrokenMaterial = 'DEFAULT' | '10 000' | '20 000' | '30 000'

export const MrpwDerogationBrokenMaterialEntries: [MrpwDerogationBrokenMaterial, { label: string }][] = [
  ['DEFAULT', { label: `3 000${nbsp}€ (par défaut)` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
  ['30 000', { label: `30 000${nbsp}€` }],
]
export const mrpwDerogationBrokenMaterialDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationBrokenMaterial',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_broken_material',
    placeholder: 'dimension_placeholder_mrpw_derogation_broken_material',
  },
  entries: MrpwDerogationBrokenMaterialEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationPreservedGoods = 'DEFAULT' | '5%' | '10%' | '15%' | '20%'

export const MrpwDerogationPreservedGoodsEntries: [MrpwDerogationPreservedGoods, { label: string }][] = [
  ['5%', { label: `5${nbsp}% du CA annuel HT` }],
  ['10%', { label: `10${nbsp}% du CA annuel HT` }],
  ['DEFAULT', { label: `15${nbsp}% du CA annuel HT (par défaut)` }],
  ['20%', { label: `20${nbsp}% du CA annuel HT` }],
]
export const mrpwDerogationPreservedGoodsDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationPreservedGoods',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_preserved_goods',
    placeholder: 'dimension_placeholder_mrpw_derogation_preserved_goods',
  },
  entries: MrpwDerogationPreservedGoodsEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationOptionPeValue = 'DEFAULT' | '18 mois' | '24 mois'

export const MrpwDerogationOptionPeValueEntries: [MrpwDerogationOptionPeValue, { label: string }][] = [
  ['DEFAULT', { label: '12 mois (par défaut)' }],
  ['18 mois', { label: '18 mois' }],
  ['24 mois', { label: '24 mois' }],
]

export const mrpwDerogationOptionPeValueDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationOptionPeValue',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_option_pe_value',
    placeholder: 'dimension_placeholder_mrpw_derogation_option_pe_value',
  },
  entries: MrpwDerogationOptionPeValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationOptionBdgValue = 'DEFAULT' | '15 000' | '20 000' | '30 000'

export const MrpwDerogationOptionBdgValueEntries: [MrpwDerogationOptionBdgValue, { label: string }][] = [
  ['DEFAULT', { label: `8 000${nbsp}€ (par défaut)` }],
  ['15 000', { label: `15 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
  ['30 000', { label: `30 000${nbsp}€` }],
]

export const mrpwDerogationOptionBdgValueDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationOptionBdgValue',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_option_bdg_value',
    placeholder: 'dimension_placeholder_mrpw_derogation_option_bdg_value',
  },
  entries: MrpwDerogationOptionBdgValueEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationOutdoorFurniture = 'DEFAULT' | '5 000' | '10 000' | '20 000'

export const MrpwDerogationOutdoorFurnitureEntries: [MrpwDerogationOutdoorFurniture, { label: string }][] = [
  ['DEFAULT', { label: `3 500${nbsp}€ (par défaut)` }],
  ['5 000', { label: `5 000${nbsp}€` }],
  ['10 000', { label: `10 000${nbsp}€` }],
  ['20 000', { label: `20 000${nbsp}€` }],
]

export const mrpwDerogationOutdoorFurnitureDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationOutdoorFurniture',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_outdoor_furniture',
    placeholder: 'dimension_placeholder_mrpw_derogation_outdoor_furniture',
  },
  entries: MrpwDerogationOutdoorFurnitureEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export type MrpwDerogationRealEstate = 'DEFAULT' | '30 000' | '40 000' | '50 000'

export const MrpwDerogationRealEstateEntries: [MrpwDerogationRealEstate, { label: string }][] = [
  ['DEFAULT', { label: `20 000${nbsp}€ (par défaut)` }],
  ['30 000', { label: `30 000${nbsp}€` }],
  ['40 000', { label: `40 000${nbsp}€` }],
  ['50 000', { label: `50 000${nbsp}€` }],
]

export const mrpwDerogationRealEstateDimension = new StringsWithDataEnumDimension({
  name: 'mrpwDerogationRealEstate',
  displayKeys: {
    name: 'dimension_name_mrpw_derogation_real_estate',
    placeholder: 'dimension_placeholder_mrpw_derogation_real_estate',
  },
  entries: MrpwDerogationRealEstateEntries,
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwHasModularityDerogationDimension = new BooleanDimension({
  name: 'mrpwHasModularityDerogation',
  displayKeys: { name: 'dimension_name_mrpw_has_modularity_derogation' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwDerogationMinMaxAssetsValueDimension = new AmountDimension({
  name: 'mrpwDerogationMinMaxAssetsValue',
  displayKeys: { name: 'dimension_name_mrpw_derogation_min_max_assets_value' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwHasDerogationMajorationV3Dimension = new BooleanDimension({
  name: 'mrpwHasDerogationMajorationV3',
  displayKeys: { name: 'dimension_name_mrpw_has_derogation_majoration_v3' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const mrpwCgRefDimension = new TextDimension({
  name: 'mrpwCgRef',
  displayKeys: { name: 'dimension_name_mrpw_cg_ref' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isMrpwV3ProductDimension = new BooleanDimension({
  name: 'isMrpwV3Product',
  displayKeys: { name: 'dimension_name_is_mrpw_v3_product' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)

export const isMrpwV3HighDeductibleAppliedForLongPeriodDimension = new BooleanDimension({
  name: 'isMrpwV3HighDeductibleAppliedForLongPeriod',
  displayKeys: { name: 'dimension_name_is_mrpw_v3_high_deductible_applied_for_long_period' },
  considerForDifferenceComputationInUi: { client: false, partner: false, platform: true },
  includeInTrackingMessages: false,
  inputLocking: undefined,
} as const)
